import {
  Box,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../../../component/icons/Icons';
import {
  GetDetailResponse,
  IReportEntry,
  IRkvConfigurationEntry,
} from '../../../generated/ApiClient';
import { ModelImage } from '../../deals/ModelImage';
import { formatToEuro } from '../../deals/new/finances/FinancesCalculationList';
import { OverlayDissolveDetail } from './OverlayDissolveDetail';
import { DateTimeFormatter, dateFormat } from '../../../common/formatter/DateTimeFormatter';
import {
  formatDissolveDate,
  getEndOfMonth,
  getValueWithVatCoefficientApplied,
  isSameDay,
} from './OverlayDissolveValues.utils';
import { confirmHandler } from '../../../common/ConfirmModal';
import { OverlaySurcharge } from './OverlaySurcharge';

interface Props {
  cmsId: string | undefined;
  report?: IReportEntry;
  rkvConfiguration?: IRkvConfigurationEntry;
  canViewDetail?: boolean;
  detailData: GetDetailResponse;
  showVatSwitch: boolean;
  pricesIncludingVAT: boolean;
  onShowPricesIncludingVatChanged: (newValue: boolean) => void;
}

export enum DissolveValueSelection {
  None,
  PreviousMonth,
  FirstDissolve,
  SecondDissolve,
  ThirdDissolve,
  Residual,
}

export interface DissolveValueEntry {
  label?: string;
  value: number;
  type: DissolveValueSelection;
  date?: Date;
  surchargeCancellationPossible?: boolean;
}

const surchargeRelative = 0.025;
const relevantCode = 'UCARSPKW';
const relevantGarant = 'Mercedes-Benz Österreich GmbH';

export function OverlayDissolveValues({
  report,
  cmsId,
  rkvConfiguration,
  canViewDetail,
  detailData,
  showVatSwitch,
  pricesIncludingVAT,
  onShowPricesIncludingVatChanged,
}: Props) {
  const { t } = useTranslation();

  const dissolve = detailData.disolve;
  const vehicle = detailData.vehicle;
  const contractEndDate = detailData.contract.contractEndDate;
  const config = detailData.configuration;

  const [selected, setSelected] = useState(DissolveValueSelection.None);

  const showContactRkvNote =
    !!rkvConfiguration?.contact && rkvConfiguration?.disableOverlayDissolveDetail;

  const isMboeCar = report?.rkv?.includes(relevantCode) && report.rkv.includes(relevantGarant);

  const vatCoefficient = pricesIncludingVAT && config?.rate ? config?.rate : 1;

  const enableDetail = cmsId && report?.listPrice && canViewDetail;

  const surcharge = (report?.listPrice ?? 0) * surchargeRelative;

  const dissolveValues: DissolveValueEntry[] = [
    {
      date: getEndOfMonth(dissolve?.dueDate, -1),
      value: dissolve?.previousMonth ?? 0,
      type: DissolveValueSelection.PreviousMonth,
      surchargeCancellationPossible: isSurchargeCancellationPossible(
        getEndOfMonth(dissolve?.dueDate, -1)
      ),
    },
    {
      date: getEndOfMonth(dissolve?.dueDate),
      value: dissolve?.dissolveValue ?? 0,
      type: DissolveValueSelection.FirstDissolve,
      surchargeCancellationPossible: isSurchargeCancellationPossible(
        getEndOfMonth(dissolve?.dueDate)
      ),
    },
    {
      date: getEndOfMonth(dissolve?.dueDate, 1),
      value: dissolve?.nextMonth ?? 0,
      type: DissolveValueSelection.SecondDissolve,
      surchargeCancellationPossible: isSurchargeCancellationPossible(
        getEndOfMonth(dissolve?.dueDate, 1)
      ),
    },
    {
      date: getEndOfMonth(dissolve?.dueDate, 2),
      value: dissolve?.theMonthAfterNextMonth ?? 0,
      type: DissolveValueSelection.ThirdDissolve,
      surchargeCancellationPossible: isSurchargeCancellationPossible(
        getEndOfMonth(dissolve?.dueDate, 2)
      ),
    },
    {
      label: t('dashboard.contractsOverlay.dissolveValues.residualValue'),
      value: report?.rediualValue ?? 0,
      type: DissolveValueSelection.Residual,
      surchargeCancellationPossible: true,
    },
  ];

  function isSurchargeCancellationPossible(dissolveDate: Date | undefined): boolean | undefined {
    if (!isMboeCar) return false;

    const lastDayOfContractEndMonth = getEndOfMonth(contractEndDate);
    const allowedDates = [
      getEndOfMonth(contractEndDate, -3),
      getEndOfMonth(contractEndDate, -2),
      getEndOfMonth(contractEndDate, -1),
      lastDayOfContractEndMonth,
      getEndOfMonth(contractEndDate, 1),
      getEndOfMonth(contractEndDate, 2),
      getEndOfMonth(contractEndDate, 3),
    ];

    const isSurchargeCancellationPossible =
      dissolveDate && allowedDates.some((date) => isSameDay(date, dissolveDate));

    return isSurchargeCancellationPossible;
  }

  return (
    <Stack gap={2} pb="200px">
      <Grid container {...dashboardGridContainer}>
        <Grid {...dashboardGridColumn} xs={12}>
          <Stack gap={3}>
            <Typography sx={{ wordWrap: 'break-word', mt: 2, fontWeight: 'bold' }}>
              {t('dashboard.contractsOverlay.dissolveInfo')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'left' }}>
              <FormControlLabel
                onChange={() => onShowPricesIncludingVatChanged(!pricesIncludingVAT)}
                control={<Switch checked={pricesIncludingVAT} />}
                label={t('dashboard.contractsOverlay.pricesIncludingVAT')}
                disabled={!showVatSwitch}
              />
            </Box>

            {dissolveValues.map((item) => {
              const hasItemValue = item.value > 0;
              const isPreviousMonth = item.type === DissolveValueSelection.PreviousMonth;
              return (
                <Stack>
              
                  {isPreviousMonth && (
                    <Typography sx={{ wordWrap: 'break-word', fontWeight: 'bold' }}>
                      {t('dashboard.contractsOverlay.dissolveValues.previousMonthHeading')}
                    </Typography>
                  )}
                  <Stack
                    key={item.type}
                    flexDirection="row"
                    gap={3}
                    sx={{
                      opacity:
                        selected !== DissolveValueSelection.None && selected !== item.type
                          ? 0.5
                          : 1,
                    }}
                  >
                    <Typography lineHeight="2em">
                      {item.label ?? formatDissolveDate(item.date)}
                    </Typography>
                    <Typography lineHeight="2em" flexGrow={1} textAlign="right">
                      {formatToEuro(getValueWithVatCoefficientApplied(item.value, vatCoefficient))}
                    </Typography>
                    {enableDetail && (
                      <IconButton
                        onClick={() => {
                          if (item.type === DissolveValueSelection.PreviousMonth) {
                            confirmHandler.confirm(
                              t('dashboard.contractsOverlay.dissolveValues.previousMonthConfirm'),
                              () => setSelected(item.type),
                            );
                          } else {
                            setSelected(item.type);
                          }
                        }}
                        {...(!hasItemValue && { disabled: true })}
                        sx={{ visibility: hasItemValue ? 'visible' : 'hidden' }}
                      >
                        {Icons.chart()}
                      </IconButton>
                    )}
                  </Stack>
                  {isPreviousMonth && <Divider />}
                </Stack>
              );
            })}
          </Stack>
        </Grid>
        <Grid {...dashboardGridColumn}>
          <Stack>
            {vehicle?.modelId && <ModelImage modelId={vehicle?.modelId} />}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 0.5, alignItems: 'center' }}>
              <Typography>{t('dashboard.contractsOverlay.dissolveValues.rkv')}</Typography>
              <Typography whiteSpace="pre">{report?.rkv || '-'}</Typography>
              {isMboeCar && (
                <>
                  <Typography>{t('dashboard.contractsOverlay.dissolveValues.vehiclePick')}</Typography>
                  <Stack flexDirection="row" alignItems="center" gap={1}>
                    {dissolve?.pickDate ? (
                      <>
                        {Icons.checked()}
                        <Typography>
                          {t('dashboard.contractsOverlay.dissolveValues.pickDate', {
                            date: DateTimeFormatter.format(dissolve.pickDate, { format: dateFormat }),
                          })}
                        </Typography>
                      </>
                    ) : (
                      <Typography>
                        {t('dashboard.contractsOverlay.dissolveValues.notPicked')}
                      </Typography>
                    )}
                  </Stack>
                </>
              )}
            </Box>
          </Stack>
        </Grid>
      </Grid>
      <Divider />
      {showContactRkvNote ? (
        <Grid {...dashboardGridColumn}>
          <Typography>{rkvConfiguration.contact}</Typography>
        </Grid>
      ) : enableDetail ? (
        <OverlayDissolveDetail
          cmsId={cmsId}
          selected={selected}
          dissolveEntries={dissolveValues}
          surcharge={surcharge}
          pricesIncludingVAT={pricesIncludingVAT}
          vatCoefficient={vatCoefficient}
          showPurchaseByOption={!isMboeCar}
          contractEndDate={contractEndDate}
          detailData={detailData}
        />
      ) : (
        <OverlaySurcharge
          dissolve={dissolve}
          report={report}
          isMboeCar={isMboeCar}
          vatCoefficient={vatCoefficient}
        />
      )}
    </Stack>
  );
}

export const dashboardGridColumn = {
  xs: 12,
  sm: 12,
  md: 12,
  lg: 12,
  xl: 6,
};

export const dashboardGridContainer = {
  spacing: 4,
};
